/**
 * A pack is a declarative, versioned business-type configuration (FR-TEN-03).
 * Applying a pack seeds a tenant's settings; the core engine never branches on pack ID.
 */

export interface LocalizedText {
  en: string;
  bn: string;
}

export const MODULES = [
  "variants",
  "batches",
  "expiry",
  "serials",
  "vat",
  "multi_branch",
  "storefront",
  "loans",
] as const;
export type ModuleKey = (typeof MODULES)[number];
export type ModuleFlags = Record<ModuleKey, boolean>;

export interface UnitDef {
  /** Stable code used in data, e.g. "kg". */
  code: string;
  name: LocalizedText;
  /** Whether fractional quantities are allowed (kg yes, piece no). */
  fractional: boolean;
}

/** Fixed conversion between two units, e.g. 1 kg = 1000 g. Item-specific packs (box = 10 strips) live on items. */
export interface UnitConversionDef {
  from: string;
  to: string;
  /** Number of `to` units in one `from` unit. */
  factor: number;
}

export type CustomFieldType = "text" | "number" | "date" | "select" | "boolean";

export interface CustomFieldDef {
  key: string;
  entity: "item" | "customer" | "supplier";
  type: CustomFieldType;
  label: LocalizedText;
  required: boolean;
  searchable: boolean;
  options?: readonly { value: string; label: LocalizedText }[];
}

export type DashboardWidget =
  | "sales_today"
  | "gross_profit"
  | "cash_in_hand"
  | "dues_receivable"
  | "dues_payable"
  | "low_stock"
  | "near_expiry"
  | "sales_trend";

export interface PackDef {
  id: string;
  version: number;
  /** Packs not yet released are shown as "coming soon" in onboarding (FR-ONB-01). */
  available: boolean;
  name: LocalizedText;
  description: LocalizedText;
  icon: string;
  modules: ModuleFlags;
  baseUnits: readonly UnitDef[];
  unitConversions: readonly UnitConversionDef[];
  categories: readonly LocalizedText[];
  customFields: readonly CustomFieldDef[];
  /** Attributes offered when adding variants, e.g. Size: S, M, L for clothing. */
  variantPresets?: readonly { name: LocalizedText; values: readonly string[] }[];
  /** Warranty new items start with, months (electronics). */
  defaultWarrantyMonths?: number;
  /** Price lists to start with, e.g. Dealer and Retailer for wholesalers. */
  priceTiers?: readonly LocalizedText[];
  receiptTemplate: "retail" | "pharmacy";
  dashboard: readonly DashboardWidget[];
  /** Days before expiry that count as "near expiry" by default. */
  nearExpiryDays?: number;
}
